import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Check, Plus, Trash2, X, Loader2, Instagram, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { fetchRegistrationConfig, submitApplication, publicImagekitAuth, fetchLocations } from '../lib/api';
import { FILTER_PRODUCT_KITS } from '../lib/constants';
import ImageKitUploader from '../admin/ImageKitUploader';

const TECHNIQUES = ['HD Makeup', 'Airbrush'];
const PRICE_TECH = { HD: 'HD Makeup', Airbrush: 'Airbrush' };

const emptyCert = () => ({ institution: '', other_institution: '', course: '', year: '', details: '', certificate_url: '' });

const Field = ({ label, children, required, hint }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-600">{label}{required && <span className="text-[#eb5381]"> *</span>}</span>
    {hint && <span className="block text-xs text-neutral-400 mt-0.5">{hint}</span>}
    <div className="mt-1.5">{children}</div>
  </label>
);

const inputCls = 'w-full bg-white border border-[#eaeaea] focus:border-[#eb5381] outline-none px-3 py-2.5 text-sm';

export default function ArtistRegister() {
  const [cfg, setCfg] = useState(null);
  const [locations, setLocations] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [f, setF] = useState({
    name: '', phone: '', email: '', city: '', state: '', instagram: '',
    years_experience: '', starting_price: '',
    occasions: [], techniques: [], product_kit: 'mixed',
    studio_location: '', home_service: true, studio_service: true, trial_available: true, hairstyling: true,
    experience_note: '',
    certifications: [emptyCert()],
    category_prices: {},
    portfolio: [],
    payment_screenshot_url: '',
    identity: { doc_type: '', doc_url: '' },
    custom_fields: {},
    commission_agreed: true,
    marketing_granted: true,
  });

  useEffect(() => {
    fetchRegistrationConfig()
      .then((c) => {
        setCfg(c);
        setF(prev => ({
          ...prev,
          commission_agreed: c.commission_consent?.default_checked ?? true,
          marketing_granted: c.marketing_consent?.default_checked ?? true,
        }));
      })
      .catch(() => toast.error('Could not load the form. Please refresh.'));
    fetchLocations().then(d => setLocations(d.states || [])).catch(() => {});
  }, []);

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const toggleArr = (k, v) => setF(prev => ({ ...prev, [k]: prev[k].includes(v) ? prev[k].filter(x => x !== v) : [...prev[k], v] }));

  const setCert = (i, k, v) => setF(prev => {
    const c = [...prev.certifications]; c[i] = { ...c[i], [k]: v }; return { ...prev, certifications: c };
  });
  const addCert = () => setF(prev => ({ ...prev, certifications: [...prev.certifications, emptyCert()] }));
  const removeCert = (i) => setF(prev => ({ ...prev, certifications: prev.certifications.filter((_, x) => x !== i) }));

  const setPrice = (occasion, tech, val) => setF(prev => {
    const cp = { ...prev.category_prices };
    const row = { ...(cp[occasion] || {}) };
    if (val === '') delete row[tech]; else row[tech] = Number(val);
    if (Object.keys(row).length) cp[occasion] = row; else delete cp[occasion];
    return { ...prev, category_prices: cp };
  });

  const addPortfolio = (urls) => setF(prev => ({ ...prev, portfolio: [...prev.portfolio, ...urls].slice(0, cfg?.portfolio_max || 10) }));
  const removePortfolio = (i) => setF(prev => ({ ...prev, portfolio: prev.portfolio.filter((_, x) => x !== i) }));
  const movePortfolio = (i, dir) => setF(prev => {
    const arr = [...prev.portfolio]; const j = i + dir;
    if (j < 0 || j >= arr.length) return prev;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...prev, portfolio: arr };
  });

  const validate = () => {
    if (f.name.trim().length < 2) return 'Please enter your name';
    if (!/^[0-9+\-\s()]{6,20}$/.test(f.phone.trim())) return 'Please enter a valid phone number';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return 'Please enter a valid email';
    if (!f.state) return 'Please select your state';
    if (!f.city) return 'Please select your city';
    if (!f.portfolio.length) return 'Please upload at least one portfolio image';
    if (cfg?.commission_consent?.mandatory && !f.commission_agreed) return 'Please agree to the commission terms';
    if (cfg?.identity_verification?.enabled && cfg.identity_verification.required && !f.identity.doc_url) return 'Identity document is required';
    for (const cf of (cfg?.custom_fields || [])) {
      if (cf.required && cf.type !== 'checkbox' && !String(f.custom_fields[cf.id] ?? '').trim()) return `Please fill "${cf.label}"`;
    }
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...f,
        years_experience: f.years_experience === '' ? null : Number(f.years_experience),
        starting_price: f.starting_price === '' ? null : Number(f.starting_price),
        certifications: f.certifications.filter(c => c.institution || c.other_institution || c.course),
        identity: f.identity.doc_url ? f.identity : null,
      };
      await submitApplication(payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="register-success" className="bg-white min-h-screen pt-32 pb-24">
        <div className="container-edge max-w-2xl text-center">
          <div className="w-16 h-16 rounded-full bg-[#eb5381] text-white grid place-items-center mx-auto"><Check className="w-8 h-8" /></div>
          <h1 className="mt-8 font-serif text-4xl md:text-5xl">Application received</h1>
          <p className="mt-5 text-neutral-600 leading-relaxed">
            Thank you for applying to FindYourMUA. Your profile is now <strong>pending review</strong> — our editorial team personally reviews every application, usually within 48 hours.
          </p>
          <div className="mt-8 bg-[#fff8ee] border border-[#ffe0b0] p-6">
            <p className="text-sm text-neutral-700">While you wait, please follow us on Instagram for updates and features:</p>
            <a href="https://instagram.com/findyour.mua" target="_blank" rel="noreferrer" data-testid="follow-instagram-btn"
              className="mt-4 inline-flex items-center gap-2 bg-black text-white px-6 py-3 text-[11px] uppercase tracking-[0.25em] hover:bg-[#eb5381] transition-colors">
              <Instagram className="w-4 h-4" /> Follow @findyour.mua
            </a>
          </div>
          <Link to="/" className="mt-8 inline-block text-[11px] uppercase tracking-[0.25em] text-[#eb5381]">← Back to home</Link>
        </div>
      </div>
    );
  }

  if (!cfg) return <div className="min-h-screen grid place-items-center text-sm uppercase tracking-[0.25em] text-neutral-400">Loading form…</div>;

  const activeCerts = (cfg.certification_options || []).filter(o => o.active);
  const airbrushSelected = f.techniques.includes('Airbrush');
  const identity = cfg.identity_verification || {};
  const payment = cfg.payment_screenshot || {};

  return (
    <div data-testid="artist-register-page" className="bg-white">
      <section className="bg-[#eb5381] text-white pt-32 pb-16">
        <div className="container-edge max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] opacity-90"><Sparkles className="w-4 h-4" /> Artist Registration</div>
          <h1 className="mt-5 font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.98]">Join FindYourMUA</h1>
          <p className="mt-5 text-white/90 leading-relaxed">Takes about 5 minutes. Our team reviews every application before your profile goes live.</p>
        </div>
      </section>

      <section className="container-edge max-w-5xl mx-auto py-14 md:py-20">
        <div className="space-y-12">
          {/* Contact */}
          <div className="space-y-5">
            <h2 className="font-serif text-2xl border-b border-[#eaeaea] pb-2">Your details</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name" required><input data-testid="reg-name" className={inputCls} value={f.name} onChange={e => set('name', e.target.value)} /></Field>
              <Field label="Phone number" required><input data-testid="reg-phone" className={inputCls} value={f.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 …" /></Field>
              <Field label="Email" required><input data-testid="reg-email" type="email" className={inputCls} value={f.email} onChange={e => set('email', e.target.value)} /></Field>
              <Field label="State" required>
                <select data-testid="reg-state" className={inputCls} value={f.state} onChange={e => setF(prev => ({ ...prev, state: e.target.value, city: '' }))}>
                  <option value="">Select state</option>
                  {locations.map(s => <option key={s.state} value={s.state}>{s.state}</option>)}
                </select>
              </Field>
              <Field label="City" required>
                <select data-testid="reg-city" className={inputCls} value={f.city} onChange={e => set('city', e.target.value)} disabled={!f.state}>
                  <option value="">{f.state ? 'Select city' : 'Select a state first'}</option>
                  {(locations.find(s => s.state === f.state)?.cities || []).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Instagram handle"><input data-testid="reg-instagram" className={inputCls} value={f.instagram} onChange={e => set('instagram', e.target.value)} placeholder="@yourhandle" /></Field>
              <Field label="Studio location / area"><input data-testid="reg-studio" className={inputCls} value={f.studio_location} onChange={e => set('studio_location', e.target.value)} /></Field>
              <Field label="Years of experience"><input data-testid="reg-years" type="number" min="0" className={inputCls} value={f.years_experience} onChange={e => set('years_experience', e.target.value)} /></Field>
              <Field label="Starting price (₹)" hint="Your bridal / starting package"><input data-testid="reg-price" type="number" min="0" className={inputCls} value={f.starting_price} onChange={e => set('starting_price', e.target.value)} /></Field>
            </div>
          </div>

          {/* Specialities */}
          <div className="space-y-5">
            <h2 className="font-serif text-2xl border-b border-[#eaeaea] pb-2">Your work</h2>
            <Field label="Occasions you serve">
              <div className="flex flex-wrap gap-2">
                {(cfg.occasions || []).map(o => (
                  <button type="button" key={o} data-testid={`reg-occasion-${o}`} onClick={() => toggleArr('occasions', o)}
                    className={`px-3 py-1.5 text-xs border transition-colors ${f.occasions.includes(o) ? 'bg-[#eb5381] text-white border-[#eb5381]' : 'border-[#eaeaea] hover:border-[#eb5381]'}`}>{o}</button>
                ))}
              </div>
            </Field>
            <Field label="Techniques">
              <div className="flex flex-wrap gap-2">
                {TECHNIQUES.map(o => (
                  <button type="button" key={o} onClick={() => toggleArr('techniques', o)}
                    className={`px-3 py-1.5 text-xs border transition-colors ${f.techniques.includes(o) ? 'bg-[#eb5381] text-white border-[#eb5381]' : 'border-[#eaeaea] hover:border-[#eb5381]'}`}>{o}</button>
                ))}
              </div>
            </Field>
            <Field label="Product kit" hint="Pick the kit that best matches the brands you use">
              <select data-testid="reg-kit" className={inputCls} value={f.product_kit} onChange={e => set('product_kit', e.target.value)}>
                {FILTER_PRODUCT_KITS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
              <div className="mt-3 space-y-2" data-testid="kit-brands">
                {['luxury', 'mixed', 'affordable'].map(k => (
                  (cfg.product_kit_brands?.[k]?.length) ? (
                    <div key={k} className={`text-xs border px-3 py-2 ${f.product_kit === k ? 'border-[#eb5381] bg-[#fff5f8]' : 'border-[#eee]'}`}>
                      <span className="uppercase tracking-[0.15em] text-[#eb5381] font-medium">{k}</span>
                      <span className="text-neutral-500"> — {cfg.product_kit_brands[k].join(', ')}</span>
                    </div>
                  ) : null
                ))}
              </div>
            </Field>

            {/* Pricing per occasion × technique */}
            <Field label="Your pricing (₹)" hint="Enter prices for the packages you offer. Airbrush column appears when you offer Airbrush.">
              <div className="overflow-x-auto border border-[#eaeaea]" data-testid="pricing-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#fafafa] text-left text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                      <th className="px-3 py-2">Occasion</th>
                      <th className="px-3 py-2">HD</th>
                      {airbrushSelected && <th className="px-3 py-2">Airbrush</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(cfg.pricing_occasions || []).map(occ => (
                      <tr key={occ} className="border-t border-[#f0f0f0]">
                        <td className="px-3 py-2 whitespace-nowrap">{occ}</td>
                        <td className="px-3 py-2">
                          <input type="number" min="0" data-testid={`price-${occ}-HD`} className="w-28 border border-[#eaeaea] focus:border-[#eb5381] outline-none px-2 py-1.5"
                            value={f.category_prices?.[occ]?.HD ?? ''} onChange={e => setPrice(occ, 'HD', e.target.value)} placeholder="—" />
                        </td>
                        {airbrushSelected && (
                          <td className="px-3 py-2">
                            <input type="number" min="0" data-testid={`price-${occ}-Airbrush`} className="w-28 border border-[#eaeaea] focus:border-[#eb5381] outline-none px-2 py-1.5"
                              value={f.category_prices?.[occ]?.Airbrush ?? ''} onChange={e => setPrice(occ, 'Airbrush', e.target.value)} placeholder="—" />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Field>

            <div className="flex flex-wrap gap-5 text-sm">
              {[['home_service', 'Home service'], ['studio_service', 'Studio service'], ['trial_available', 'Trials available'], ['hairstyling', 'Hairstyling']].map(([k, lbl]) => (
                <label key={k} className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={f[k]} onChange={e => set(k, e.target.checked)} /> {lbl}
                </label>
              ))}
            </div>
          </div>

          {/* Certifications */}
          <div className="space-y-5">
            <h2 className="font-serif text-2xl border-b border-[#eaeaea] pb-2">Certification & training</h2>
            {f.certifications.map((c, i) => (
              <div key={i} data-testid={`cert-entry-${i}`} className="border border-[#eaeaea] p-4 space-y-4 relative">
                {f.certifications.length > 1 && (
                  <button type="button" onClick={() => removeCert(i)} data-testid={`cert-remove-${i}`} className="absolute top-3 right-3 text-neutral-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Makeup artist / academy / institution">
                    <select data-testid={`cert-institution-${i}`} className={inputCls} value={c.institution} onChange={e => setCert(i, 'institution', e.target.value)}>
                      <option value="">Select</option>
                      {activeCerts.map(o => <option key={o.id} value={o.label}>{o.label}</option>)}
                    </select>
                  </Field>
                  {c.institution === 'Other' && (
                    <Field label="Please specify the makeup artist, academy, institution, or certification name">
                      <input data-testid={`cert-other-${i}`} className={inputCls} value={c.other_institution} onChange={e => setCert(i, 'other_institution', e.target.value)} />
                    </Field>
                  )}
                  <Field label="Course / certification name"><input className={inputCls} value={c.course} onChange={e => setCert(i, 'course', e.target.value)} /></Field>
                  <Field label="Year completed"><input className={inputCls} value={c.year} onChange={e => setCert(i, 'year', e.target.value)} /></Field>
                </div>
                <Field label="Additional details"><input className={inputCls} value={c.details} onChange={e => setCert(i, 'details', e.target.value)} /></Field>
                <Field label="Certificate upload (optional)">
                  {c.certificate_url
                    ? <div className="flex items-center gap-2 text-sm text-[#16a34a]"><Check className="w-4 h-4" /> Uploaded <button type="button" className="text-neutral-400 hover:text-red-500 ml-2" onClick={() => setCert(i, 'certificate_url', '')}><X className="w-3 h-3" /></button></div>
                    : <ImageKitUploader accept="document" compact folder="findyourmua/registrations/certificates" authFn={publicImagekitAuth} label="Upload certificate" onUploaded={(u) => setCert(i, 'certificate_url', u[0])} testId={`cert-upload-${i}`} />}
                </Field>
              </div>
            ))}
            <button type="button" onClick={addCert} data-testid="add-cert-btn" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#eb5381]"><Plus className="w-4 h-4" /> Add another certification</button>

            <Field label="Share your experience, achievements, awards, training, qualifications, or anything else that helps us understand your work as a makeup artist.">
              <textarea data-testid="reg-experience-note" rows={4} className={inputCls} value={f.experience_note} onChange={e => set('experience_note', e.target.value)} />
            </Field>
          </div>

          {/* Portfolio */}
          <div className="space-y-4">
            <h2 className="font-serif text-2xl border-b border-[#eaeaea] pb-2">Portfolio ({f.portfolio.length}/{cfg.portfolio_max}) <span className="text-[#eb5381]">*</span></h2>
            <p className="text-xs text-neutral-500">High-quality images only · Clear face / mid shots · No heavy filters · No logos or text.</p>
            {f.portfolio.length < cfg.portfolio_max && (
              <ImageKitUploader accept="image" multiple folder="findyourmua/registrations/portfolio" authFn={publicImagekitAuth} label="Upload portfolio images" onUploaded={addPortfolio} testId="portfolio-uploader" />
            )}
            {f.portfolio.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="portfolio-previews">
                {f.portfolio.map((src, i) => (
                  <div key={src + i} className="relative group aspect-square bg-neutral-100">
                    <img src={src} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePortfolio(i)} data-testid={`portfolio-remove-${i}`} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                    <div className="absolute bottom-1 left-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button type="button" onClick={() => movePortfolio(i, -1)} className="bg-black/70 text-white text-xs px-1.5 rounded">←</button>
                      <button type="button" onClick={() => movePortfolio(i, 1)} className="bg-black/70 text-white text-xs px-1.5 rounded">→</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Identity */}
          {identity.enabled && (
            <div className="space-y-4">
              <h2 className="font-serif text-2xl border-b border-[#eaeaea] pb-2">{identity.label} <span className="text-[#eb5381]">*</span></h2>
              <p className="text-xs text-neutral-500">{identity.instructions}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Document type">
                  <select data-testid="identity-type" className={inputCls} value={f.identity.doc_type} onChange={e => set('identity', { ...f.identity, doc_type: e.target.value })}>
                    <option value="">Select</option>
                    {(identity.accepted_docs || []).map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label="Upload document">
                  {f.identity.doc_url
                    ? <div className="flex items-center gap-2 text-sm text-[#16a34a]"><Check className="w-4 h-4" /> Uploaded <button type="button" className="text-neutral-400 hover:text-red-500 ml-2" onClick={() => set('identity', { ...f.identity, doc_url: '' })}><X className="w-3 h-3" /></button></div>
                    : <ImageKitUploader accept="document" compact folder="findyourmua/registrations/identity" authFn={publicImagekitAuth} label="Upload ID (image / PDF)" onUploaded={(u) => set('identity', { ...f.identity, doc_url: u[0] })} testId="identity-upload" />}
                </Field>
              </div>
              <p className="text-[11px] text-neutral-400">{identity.consent_text}</p>
            </div>
          )}

          {/* Payment screenshot (admin-toggled) */}
          {payment.enabled && (
            <div className="space-y-3">
              <h2 className="font-serif text-2xl border-b border-[#eaeaea] pb-2">{payment.label}{payment.required ? ' *' : ''}</h2>
              <div className="bg-[#fff8ee] border border-[#ffe0b0] p-4">
                <p className="text-sm text-neutral-700">{payment.instructions}</p>
                <div className="mt-3 flex flex-wrap gap-x-8 gap-y-1 text-sm">
                  {payment.amount > 0 && <div><span className="text-neutral-500">Amount:</span> <strong data-testid="payment-amount">₹{payment.amount}</strong></div>}
                  {payment.pay_to && <div><span className="text-neutral-500">Pay to:</span> <strong data-testid="payment-payto">{payment.pay_to}</strong></div>}
                </div>
              </div>
              {f.payment_screenshot_url
                ? <div className="flex items-center gap-2 text-sm text-[#16a34a]"><Check className="w-4 h-4" /> Screenshot uploaded <button type="button" className="text-neutral-400 hover:text-red-500 ml-2" onClick={() => set('payment_screenshot_url', '')}><X className="w-3 h-3" /></button></div>
                : <ImageKitUploader accept="image" compact folder="findyourmua/registrations/payments" authFn={publicImagekitAuth} label="Upload payment screenshot" onUploaded={(u) => set('payment_screenshot_url', u[0])} testId="payment-upload" />}
            </div>
          )}

          {/* Custom admin-defined fields */}
          {(cfg.custom_fields || []).length > 0 && (
            <div className="space-y-5" data-testid="custom-fields-section">
              <h2 className="font-serif text-2xl border-b border-[#eaeaea] pb-2">A little more</h2>
              {cfg.custom_fields.map(cf => {
                const val = f.custom_fields[cf.id] ?? (cf.type === 'checkbox' ? false : '');
                const setVal = (v) => set('custom_fields', { ...f.custom_fields, [cf.id]: v });
                if (cf.type === 'checkbox') {
                  return (
                    <label key={cf.id} className="flex gap-3 items-start cursor-pointer" data-testid={`custom-${cf.id}`}>
                      <input type="checkbox" className="mt-1" checked={!!val} onChange={e => setVal(e.target.checked)} />
                      <span className="text-sm text-neutral-700">{cf.label}{cf.required && <span className="text-[#eb5381]"> *</span>}</span>
                    </label>
                  );
                }
                return (
                  <Field key={cf.id} label={cf.label} required={cf.required} hint={cf.help}>
                    {cf.type === 'textarea' ? (
                      <textarea rows={3} className={inputCls} placeholder={cf.placeholder || ''} value={val} onChange={e => setVal(e.target.value)} data-testid={`custom-${cf.id}`} />
                    ) : cf.type === 'select' ? (
                      <select className={inputCls} value={val} onChange={e => setVal(e.target.value)} data-testid={`custom-${cf.id}`}>
                        <option value="">Select</option>
                        {(cf.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={cf.type === 'number' ? 'number' : 'text'} className={inputCls} placeholder={cf.placeholder || ''} value={val} onChange={e => setVal(e.target.value)} data-testid={`custom-${cf.id}`} />
                    )}
                  </Field>
                );
              })}
            </div>
          )}

          {/* Consent */}
          <div className="space-y-4 bg-[#fafafa] border border-[#eaeaea] p-5">
            <label className="flex gap-3 items-start cursor-pointer">
              <input type="checkbox" data-testid="consent-commission" className="mt-1" checked={f.commission_agreed} onChange={e => set('commission_agreed', e.target.checked)} />
              <span className="text-sm text-neutral-700">{cfg.commission_consent?.text}{cfg.commission_consent?.mandatory && <span className="text-[#eb5381]"> *</span>}</span>
            </label>
            <label className="flex gap-3 items-start cursor-pointer">
              <input type="checkbox" data-testid="consent-marketing" className="mt-1" checked={f.marketing_granted} onChange={e => set('marketing_granted', e.target.checked)} />
              <span className="text-sm text-neutral-700">{cfg.marketing_consent?.text}</span>
            </label>
          </div>

          <button type="button" onClick={submit} disabled={submitting} data-testid="submit-application-btn"
            className="w-full inline-flex items-center justify-center gap-3 bg-[#eb5381] hover:bg-[#d64570] disabled:opacity-60 text-white px-10 py-4 text-[12px] uppercase tracking-[0.25em] transition-colors">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Submit application
          </button>
        </div>
      </section>
    </div>
  );
}
